import { useStore } from '@nanostores/react';
import { useLoaderData, useNavigate } from '@remix-run/react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchSiteInfo } from '~/lib/.client/api/sites';
import { clientFetch } from '~/lib/.client/fetch';
import { chatHooks } from '~/lib/chat-hooks';
import { githubBranchSwitchMessage } from '~/lib/client-github';
import { BOLT_IMPORT_ID } from '~/lib/constants';
import { CloneStateKind, useGithubStore } from '~/lib/context/github';
import { getChatHistory, type ChatHistory } from '~/lib/persistence/chats';
import { hydrate, importStore, type APIProjectData, type ProjectData } from '~/lib/projects';
import { importModifications, importPromise } from '~/lib/projects/importStore';
import { sendSentryError } from '~/lib/sentry.client';
import { snapshotManager } from '~/lib/snapshots/snapshot-manager';
import { setDescription } from '~/lib/stores/description';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import type { loader } from '~/routes/_chat.~.$slug';
import type { Message } from '~/types/chat';
import { effectiveTier, type EffectiveTier } from '~/utils/effectiveTier';
import { NOOP } from '~/utils/noops';
import { prettyEncodeComponent } from '~/utils/prettyEncodeComponent';
import { wait, withResolvers } from '~/utils/promises';
import { stripIndents } from '~/utils/stripIndent';

export function useProjectImport() {
  const { slug, projectId, projectInfo } = useLoaderData<typeof loader>();
  const user = useStore(userStore.user);

  const navigate = useNavigate();

  const [ready, setReady] = useState(false);

  const deferredChatHistory = withResolvers<ChatHistory>();

  const github = useGithubStore();

  const cloning = github.state.get().clone.kind !== CloneStateKind.Ignore;

  let userTier: EffectiveTier | undefined;

  try {
    userTier = user ? effectiveTier(user) : undefined;
  } catch {
    userTier = undefined;
  }

  const supportCustomDomains = user?.featureFlags.hackathon && userTier?.plan !== 'personal';

  useEffect(() => {
    async function fetchProject(): Promise<ProjectData | { errorMessage: string }> {
      const response = await clientFetch('/api/import/stackblitz', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slug,
        }),
      });

      if (response.status === 404) {
        if (!user) {
          navigate(`/login?next=${prettyEncodeComponent(location.pathname)}`);

          throw new DOMException('This operation was aborted', 'AbortError');
        }

        // if import fails, redirect to home page and show error toast
        toast.error("Project could not be imported because it does not exist, or you don't have access to it.");
        navigate('/');

        throw new Error(`Project could not be imported because it does not exist, or you don't have access to it.`);
      }

      const result = await response.json<APIProjectData | { errorMessage: string }>();

      if ('errorMessage' in result) {
        return result;
      }

      return result;
    }

    async function fetchChatHistory() {
      let chatHistory: ChatHistory | undefined = undefined;
      let chatMessages: Message[] = [];

      if (projectId) {
        const activeOrganization = userStore.getActiveOrganization().get();
        chatHistory = await getChatHistory(projectId, activeOrganization);

        chatMessages = chatHistory?.messages ?? [];

        snapshotManager.hydrate(projectId, chatMessages);

        try {
          await snapshotManager.fetchSnapshots(projectId, new AbortController().signal);
        } catch (error) {
          console.warn('Failed to fetch snapshots:', error);
        }
      }

      return chatHistory;
    }

    function fetchDeployInformation() {
      if (!projectId) {
        return undefined;
      }

      return fetchSiteInfo(projectId);
    }

    if (projectInfo) {
      setDescription(projectInfo.title);
    }

    Promise.all([fetchProject(), fetchChatHistory(), fetchDeployInformation()])
      .then(([projectData, chatHistory, deploymentInfo]) => {
        if ('errorMessage' in projectData) {
          importPromise.reject(new Error(projectData.errorMessage));

          return;
        }

        projectStore.updateProject(projectData, userStore.getActiveOrganization().get());
        userStore.setCanSuggestSwitchAccount(projectData.organization);

        setDescription(projectData.title);

        const branchSwitch = github.branchSwitch();
        const branchMessage = branchSwitch && githubBranchSwitchMessage(branchSwitch.branchName);

        const chatHistoryMessages =
          chatHistory?.messages && chatHistory.messages.length > 0 ? chatHistory.messages : [];

        const hasHistory = chatHistoryMessages.length > 0;

        const lastAssistantMessageIsInitMessage =
          chatHistoryMessages.findLast(({ role }) => role === 'assistant')?.id === BOLT_IMPORT_ID;

        const messages = [
          ...(hasHistory ? chatHistoryMessages : [createNewInitialMessage({ slug, cloning })]),
          ...hydrate(projectData, {
            deploymentInfo,
            injectBootstrapMessage: hasHistory && !lastAssistantMessageIsInitMessage,
            supportCustomDomains,
          }),
          ...(branchMessage ? [branchMessage] : []),
        ];

        importStore.setKey('deploymentInfo', deploymentInfo);
        importStore.setKey('messages', messages);

        deferredChatHistory.resolve(chatHistory);

        chatHooks.hasMorePreviousMessages.set(chatHistory?.hasMore ?? false);

        importPromise.resolve();

        if (cloning) {
          Promise.race([wait(30_000), importModifications.promise.catch(NOOP).then(() => true)]).then((settled) => {
            if (!settled) {
              sendSentryError(new ImportModificationsError(`Timeout for ${projectId}`));
            }
          });
        }

        setReady(true);
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        importPromise.reject(error);
      });
  }, []);

  return {
    ready,
    chatHistory: deferredChatHistory.promise,
  };
}

class ImportModificationsError extends Error {
  name = 'ImportModificationsError';
}

interface CreateInitialMessageOptions {
  slug: string;
  cloning: boolean;
}

function createNewInitialMessage({ slug, cloning }: CreateInitialMessageOptions): Message {
  const importMessage = stripIndents`
    <boltArtifact id="stackblitz-import" title="Importing Bolt Project" data-component="importing_project">
      <boltAction type="import" target=${JSON.stringify(slug)} ${cloning ? 'cloning="true"' : ''}></boltAction>
    </boltArtifact>
  `;

  return {
    id: BOLT_IMPORT_ID,
    type: 'chat',
    role: 'assistant',
    status: 'complete',
    content: importMessage,
    ignore: true,
    merge: false,
    parts: [],
  };
}
